import { plainToClass } from "class-transformer";
import { DocumentLibraryLoaderWithSharepoint } from '../../../shared/components/document-library/document-library-loader';
import { EditDocumentTreeLoader, EditDocumentSection } from "../../../shared/components/document-tree/editable-document-tree/edit-document-tree"

export class SeminarSeriesPage {

    constructor(documentSections: EditDocumentSection[], targetFile: string = "", targetSearchBox: string = "") {
        let editDocumentTree = new EditDocumentTreeLoader(documentSections, targetFile, targetSearchBox);

        if (typeof window !== "undefined" && typeof document !== "undefined") {
            if (document.readyState !== "loading") {
                this.initializeCarousel();
                this.initializeExpandToggle();
                this.initializeBootstrapPopovers();
            } else {
                document.addEventListener("DOMContentLoaded", () => {
                    this.initializeCarousel();
                    this.initializeExpandToggle();
                    this.initializeBootstrapPopovers();
                });
            }
        }
    }

    private initializeExpandToggle(): void {
        const expandBtn = document.getElementById("carouselExpand") as HTMLButtonElement | null;
        const carousel = document.getElementById("seminarCarouselContainer");
        const grid = document.getElementById("seminarGridContainer");
        const arrowCol = document.querySelector(".arrow-col") as HTMLElement | null;
        if (!expandBtn || !carousel || !grid || !arrowCol) return;

        let expanded = false;

        // Helper to set button content with icon and text
        function setExpandButton(isExpanded: boolean) {
            if (isExpanded) {
                expandBtn.innerHTML = `<i class="fa fa-minimize mr-1" aria-hidden="true"></i> Collapse`;
            } else {
                expandBtn.innerHTML = `<i class="fa fa-expand mr-1" aria-hidden="true"></i> Expand`;
            }
        }

        setExpandButton(expanded);

        expandBtn.addEventListener("click", () => {
            expanded = !expanded;
            if (expanded) {
                carousel.style.display = "none";
                grid.style.display = "block";
                arrowCol.classList.add("disabled");
            } else {
                carousel.style.display = "block";
                grid.style.display = "none";
                arrowCol.classList.remove("disabled");
            }
            setExpandButton(expanded);
        });
    }


    public searchAndEnter(searchString: string, searchBoxId: string = 'seminar-series-search') {
        const input = document.getElementById(searchBoxId) as HTMLInputElement | null;
        if (!input) {
            console.warn(`searchAndEnter: No input found with id "${searchBoxId}"`);
            return;
        }

        const rect = input.getBoundingClientRect();
        const absoluteY = rect.top + window.pageYOffset - 100; // 100px padding above

        window.scrollTo({ top: absoluteY, behavior: "smooth" });

        setTimeout(() => {
            input.value = searchString;
            input.focus();
            const keyUpEvent = new KeyboardEvent('keyup', {
                bubbles: true,
                cancelable: true,
                key: 'Enter',
                code: 'Enter',
                which: 13,
                keyCode: 13
            });
            input.dispatchEvent(keyUpEvent);
        }, 420); // Wait for the scroll to finish (adjust as needed)
    }


    // --------- Added: Pure JS Grid/Flex Carousel Logic ---------
    private initializeCarousel(): void {
        const track = document.getElementById('seminarTrack');
        if (!track) return;

        // Step 1: Add no-transition for instant positioning
        track.classList.add('no-transition');

        // How many cards appear in carousel view based on window size
        function getItemsPerView() {
            if (window.innerWidth >= 992) return 3;
            else if (window.innerWidth >= 768) return 2;

            return 1;
        }

        const items = Array.from(track.children) as HTMLElement[];
        const totalItems = items.length;

        // Save the intended default index ONCE at startup from the data attribute assigned in html
        const carouselContainer = document.getElementById('seminarCarouselContainer');
        const defaultIndex = carouselContainer ? parseInt(carouselContainer.getAttribute('data-default-index') || '0', 10) : 0;
        let currentIndex = defaultIndex;

        // Controls
        const prevBtn = document.getElementById('seminarPrev');
        const nextBtn = document.getElementById('seminarNext');

        function updateArrowStates() {
            const itemsPerViewNow = getItemsPerView();
            if (prevBtn) {
                const atFirstItem = currentIndex <= 0;
                prevBtn.classList.toggle("disabled", atFirstItem);
                prevBtn.setAttribute("aria-disabled", String(atFirstItem));
            }
            if (nextBtn) {
                const atLastItem = currentIndex >= totalItems - itemsPerViewNow;
                nextBtn.classList.toggle("disabled", atLastItem);
                nextBtn.setAttribute("aria-disabled", String(atLastItem));
            }
        }

        function updateCarousel() {
            const itemsPerViewNow = getItemsPerView();
            const itemWidth = items[0].offsetWidth;
            if (currentIndex > totalItems - itemsPerViewNow) {
                currentIndex = Math.max(0, totalItems - itemsPerViewNow);
            }
            if (currentIndex < 0) {
                currentIndex = 0;
            }
            const moveX = currentIndex * itemWidth;
            track.style.transform = `translateX(-${moveX}px)`;
            updateArrowStates();
        }

        function prev() {
            if (currentIndex > 0) {
                currentIndex--;
                updateCarousel();
            }
        }

        function next() {
            const itemsPerViewNow = getItemsPerView();
            if (currentIndex < totalItems - itemsPerViewNow) {
                currentIndex++;
                updateCarousel();
            }
        }

        function reset() {
            // Set back to the original default index, not always zero!
            currentIndex = defaultIndex;
            updateCarousel();
        }

        // Desktop controls
        if (prevBtn && nextBtn) {
            prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (!prevBtn.classList.contains("disabled")) prev();
            });
            nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (!nextBtn.classList.contains("disabled")) next();
            });
        }

        // Mobile controls
        const prevBtnMobile = document.getElementById('seminarPrevMobile');
        const nextBtnMobile = document.getElementById('seminarNextMobile');
        if (prevBtnMobile && nextBtnMobile) {
            prevBtnMobile.addEventListener('click', (e) => { e.preventDefault(); prev(); });
            nextBtnMobile.addEventListener('click', (e) => { e.preventDefault(); next(); });
        }

        // Reset button
        const resetBtn = document.getElementById('carouselReset');
        if (resetBtn) {
            resetBtn.addEventListener('click', (e) => { e.preventDefault(); reset(); });
        }

        window.addEventListener('resize', () => {
            updateCarousel();
        });

        // Step 2: Instantly position carousel
        updateCarousel();

        // Step 3: Remove no-transition class after a tick,
        // so further moves (arrows) are animated
        setTimeout(() => {
            track.classList.remove('no-transition');
        }, 30);
    }
    private initializeBootstrapPopovers(): void {
        if (typeof $ !== "undefined" && typeof ($ as any).fn.popover === "function") {
            const $popovers = ($('[data-toggle="popover"]') as any).popover({
                trigger: 'manual',
                html: false
            });

            $popovers.on('click', function (e: any) {
                e.stopPropagation();
                $popovers.not(this).popover('hide');
                $(this).popover('show');
            });

            $(document).on('click', function () {
                $popovers.popover('hide');
            });
        }
    }
}